import { lazy } from 'react';

const FLAG = 'page-has-been-force-refreshed';

/**
 * Wraps a dynamic import in a `React.lazy` with one-shot retry-via-reload
 * behavior for ChunkLoadError.
 *
 * When a new deployment is released the chunk filenames change (CRA emits
 * content-hashed names). A user with the old `index.html` in memory will then
 * request a chunk URL that no longer exists, producing a ChunkLoadError.
 *
 * On the first such failure this wrapper sets a session-scoped flag and
 * triggers `window.location.reload()`. The reload fetches a fresh
 * `index.html`, which references the new chunk URLs, and the import succeeds
 * on the next mount. If the import fails again after that one reload, the
 * flag prevents an infinite loop and the original error is rethrown so it can
 * surface in an Error Boundary or the Suspense fallback.
 *
 * Usage: `const Foo = lazyWithRetry(() => import('./Foo'));`
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} componentImport
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>}
 */
export function lazyWithRetry(componentImport) {
  return lazy(async () => {
    const alreadyReloaded = JSON.parse(
      window.sessionStorage.getItem(FLAG) || 'false',
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem(FLAG, 'false');
      return component;
    } catch (error) {
      const message = error?.message || '';
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        /Loading chunk [\d]+ failed/i.test(message) ||
        /Loading CSS chunk [\d]+ failed/i.test(message);

      if (isChunkError && !alreadyReloaded) {
        window.sessionStorage.setItem(FLAG, 'true');
        window.location.reload();
        return { default: () => null };
      }

      throw error;
    }
  });
}

export default lazyWithRetry;
